/*! JD - EMPIRE Theme | Copyright (c) 2026 JDA Commerce. All rights reserved. */

/**
 * Fit finder.
 *
 * Whether this shows at all is decided here rather than in Liquid, because the
 * decision needs real string normalisation — see assets/size-normalise.js. The
 * panel is rendered collapsed and takes up no space, so deciding client-side
 * causes no visible flash.
 *
 * Clothing: height + weight give the size she normally wears, then the garment
 * offset is added (this range runs small).
 * Shoes: body measurements say nothing useful, so she gives a size she already
 * owns in a brand she knows and we add that brand's offset.
 *
 * The answer is always snapped onto a size the product is really sold in.
 */
(() => {
  const SIZE = () => window.JDSize;

  const parseBands = (raw) =>
    raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, minW, maxW, minH, maxH] = line.split('|').map((p) => (p || '').trim());
        if (!label) return null;
        const num = (v) => {
          const n = parseFloat(v);
          return Number.isFinite(n) ? n : null;
        };
        return {
          label,
          key: SIZE().normaliseApparel(label) || label.toUpperCase(),
          minW: num(minW),
          maxW: num(maxW),
          minH: num(minH),
          maxH: num(maxH)
        };
      })
      .filter((b) => b && (b.minW !== null || b.maxW !== null));

  const withinWeight = (band, w) =>
    (band.minW === null || w >= band.minW) && (band.maxW === null || w <= band.maxW);

  const withinHeight = (band, h) =>
    h === null || band.minH === null || band.maxH === null || (h >= band.minH && h <= band.maxH);

  const weightDistance = (band, w) => {
    if (band.minW !== null && w < band.minW) return band.minW - w;
    if (band.maxW !== null && w > band.maxW) return w - band.maxW;
    return 0;
  };

  /**
   * Clothing. `stocked` maps a canonical size (S, 2XL, …) to the product's own
   * label for it, so the recommendation can only ever be a size on this page.
   */
  const recommendApparel = (bands, weight, height, sizeUpSteps, relaxedFromAge, age, stocked) => {
    const inWeight = bands.filter((b) => withinWeight(b, weight));

    let picked;
    if (inWeight.length === 1) {
      picked = inWeight[0];
    } else if (inWeight.length > 1) {
      picked = inWeight.find((b) => withinHeight(b, height)) || inWeight[inWeight.length - 1];
    } else {
      picked = bands.slice().sort((a, b) => weightDistance(a, weight) - weightDistance(b, weight))[0];
    }
    if (!picked) return null;

    let index = bands.indexOf(picked) + (sizeUpSteps || 0);
    if (relaxedFromAge > 0 && age && age >= relaxedFromAge) index += 1;
    index = Math.max(0, Math.min(index, bands.length - 1));

    // Search upwards for a stocked size first, then downwards — so a product
    // that stops at 3XL recommends 3XL instead of something unbuyable.
    for (let i = index; i < bands.length; i += 1) {
      const hit = stocked.get(bands[i].key);
      if (hit) return hit;
    }
    for (let i = index - 1; i >= 0; i -= 1) {
      const hit = stocked.get(bands[i].key);
      if (hit) return hit;
    }
    return null;
  };

  /**
   * Shoes. Half sizes round UP when the product does not stock them — going
   * too small is the failure mode we are trying to avoid.
   */
  const recommendShoe = (brandSize, offset, sizes) => {
    const target = brandSize + offset;
    const sorted = sizes.slice().sort((a, b) => a.value - b.value);
    if (!sorted.length) return null;

    const exact = sorted.find((s) => Math.abs(s.value - target) < 0.001);
    if (exact) return exact.original;

    const up = sorted.find((s) => s.value > target);
    if (up) return up.original;

    return sorted[sorted.length - 1].original;
  };

  const applyToVariantPicker = (label) => {
    const norm = (s) => String(s).trim().toLowerCase().replace(/\s+/g, '');
    const target = norm(label);
    const match = [...document.querySelectorAll('[data-option-button]')].find(
      (b) => norm(b.dataset.optionValue || '') === target
    );
    if (match && !match.disabled) match.click();
  };

  /**
   * Move focus into the panel when it opens — carefully.
   *
   * Two things went wrong with the naive version:
   *
   *   1. On a phone, focusing a <select> opens the native picker immediately.
   *      On a shoe product the first field IS the brand select, so tapping
   *      "Find my size" threw the shopper straight into a brand wheel she never
   *      asked for. Touch devices therefore get no autofocus at all — the panel
   *      is already on screen and she can tap what she wants.
   *
   *   2. The shoe fields sit before the clothing fields in the DOM, so a plain
   *      querySelector matched the hidden brand select on dress products too.
   *      Only visible fields count.
   */
  const focusFirstField = (root) => {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    const field = Array.from(root.querySelectorAll('.fit-finder__input')).find(
      (el) => el.tagName !== 'SELECT' && el.offsetParent !== null
    );
    field?.focus({ preventScroll: true });
  };

  const addCompactTrigger = (root, label) => {
    if (root.dataset.compact !== 'true') return;

    const header = document.querySelector('.product-info__option-header.size');
    if (!header || document.querySelector('[data-fit-open]')) return;

    /**
     * Where the link goes.
     *
     * "under_sizes" (default): its own row directly beneath the size buttons.
     * Stacking two links in the top-right corner never sat well — the size-chart
     * app owns that corner, the pair overhangs the buttons on desktop and gets
     * cramped on mobile. A full-width row under the sizes has room at every
     * width and reads as a step in the flow rather than a footnote.
     *
     * "beside_label": the older behaviour, under the size-chart link.
     */
    const group = header.closest('.product-info__option-group');
    const values = group?.querySelector('.product-info__option-values');
    const underSizes = root.dataset.triggerPosition !== 'beside_label' && values;

    let host;
    if (underSizes) {
      host = document.createElement('div');
      host.className = 'fit-finder__trigger-row';
      values.insertAdjacentElement('afterend', host);
    } else {
      host = header.querySelector('[data-option-aside]') || header;
    }

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'fit-finder__open';
    trigger.setAttribute('data-fit-open', '');
    // A person outline, not a ruler: the size-chart app already puts a ruler
    // right next to this and two rulers read as a duplicate control.
    trigger.innerHTML =
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="6.5" r="3.2"/><path d="M5.5 20.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"/></svg>' +
      '<span>' + label + '</span>';

    trigger.addEventListener('click', () => {
      root.open = true;
      focusFirstField(root);
    });

    host.appendChild(trigger);
    root.classList.add('fit-finder--compact');
  };

  const init = (root) => {
    if (!SIZE()) return;

    const values = (root.dataset.sizeValues || '').split('|').map((v) => v.trim()).filter(Boolean);
    const minMatches = parseInt(root.dataset.minMatches, 10) || 3;
    const forced = root.dataset.mode || 'auto';

    const catalogSystem = root.dataset.catalogSystem || 'auto';
    const classified = SIZE().classify(values, minMatches, catalogSystem);
    const kind = forced === 'auto' ? classified.kind : forced;

    // Nothing recognisable on this product — remove the panel entirely so it
    // cannot be opened by anything else, and never add the trigger.
    if (kind === 'none' || classified.kind === 'none' || !classified.sizes.length) {
      root.remove();
      return;
    }

    const bandsNode = root.querySelector('[data-fit-bands]');
    const bands = bandsNode
      ? parseBands(bandsNode.innerHTML.replace(/&amp;/g, '&').replace(/<br\s*\/?>/g, '\n'))
      : [];

    const stocked = new Map();
    classified.sizes.forEach((s) => {
      if (!stocked.has(s.key)) stocked.set(s.key, s.original);
    });

    const isShoes = kind === 'shoes';
    root.dataset.resolvedMode = kind;
    root.classList.add(isShoes ? 'fit-finder--shoes' : 'fit-finder--apparel');
    root.querySelectorAll('[data-fit-when]').forEach((el) => {
      el.hidden = el.dataset.fitWhen !== kind;
    });

    if (!isShoes && !bands.length) {
      root.remove();
      return;
    }

    /* The merchant's size bands are written in ONE unit system (data-units).
       The shopper may think in another. So there are two systems in play:
         bandUnit   — what the bands mean, fixed
         inputUnit  — what the shopper is typing, follows her region choice
       Everything gets converted to bandUnit before any comparison happens. */
    const bandUnitMetric = root.dataset.units === 'metric';
    const systemSelect = root.querySelector('[data-fit-system]');
    const METRIC_REGIONS = ['uk', 'eu', 'au'];
    const inputIsMetric = () => METRIC_REGIONS.indexOf((systemSelect?.value || 'us').toLowerCase()) !== -1;

    const applyUnitUI = () => {
      const want = inputIsMetric() ? 'metric' : 'imperial';
      root.querySelectorAll('[data-fit-unit]').forEach((el) => {
        el.hidden = el.dataset.fitUnit !== want;
      });
      const w = root.querySelector('[data-fit-weight]');
      if (w) w.placeholder = inputIsMetric() ? '68' : '150';
    };
    applyUnitUI();
    systemSelect?.addEventListener('change', applyUnitUI);

    const sizeUpSteps = parseInt(root.dataset.sizeUp, 10) || 0;
    const relaxedFromAge = parseInt(root.dataset.relaxedAge, 10) || 0;

    const submit = root.querySelector('[data-fit-submit]');
    const result = root.querySelector('[data-fit-result]');
    const resultSize = root.querySelector('[data-fit-result-size]');
    const error = root.querySelector('[data-fit-error]');
    if (!submit || !result || !resultSize) return;

    const fail = (message) => {
      result.hidden = true;
      error.textContent = message;
      error.hidden = false;
    };

    const readHeight = () => {
      let cm = null;
      if (inputIsMetric()) {
        const v = parseFloat(root.querySelector('[data-fit-height-cm]')?.value);
        cm = Number.isFinite(v) ? v : null;
      } else {
        const ft = parseFloat(root.querySelector('[data-fit-height-ft]')?.value);
        const inch = parseFloat(root.querySelector('[data-fit-height-in]')?.value) || 0;
        cm = Number.isFinite(ft) ? SIZE().inToCm(ft * 12 + inch) : null;
      }
      if (cm === null) return null;
      return bandUnitMetric ? cm : SIZE().cmToIn(cm);
    };

    const readWeight = () => {
      const v = parseFloat(root.querySelector('[data-fit-weight]')?.value);
      if (!Number.isFinite(v)) return null;
      const kg = inputIsMetric() ? v : SIZE().lbToKg(v);
      return bandUnitMetric ? kg : SIZE().kgToLb(kg);
    };

    // Ranges are deliberately generous but finite: without them "45 ft, 4 lb"
    // produced a confident, meaningless answer.
    const limitsFor = () => (inputIsMetric()
      ? { hMin: 120, hMax: 215, wMin: 32, wMax: 200, hUnit: 'cm', wUnit: 'kg' }
      : { hMin: 48, hMax: 84, wMin: 70, wMax: 440, hUnit: 'in', wUnit: 'lb' });

    submit.addEventListener('click', () => {
      let size;

      if (isShoes) {
        const rawBrandSize = parseFloat(root.querySelector('[data-fit-brand-size]')?.value);
        const shopperSystem = root.querySelector('[data-fit-system]')?.value || 'us';
        // The shopper types a size in HER system; everything downstream compares
        // against the canonical US scale, so convert before doing anything else.
        const brandSize = Number.isFinite(rawBrandSize)
          ? SIZE().shoeToCanonical(rawBrandSize, shopperSystem)
          : rawBrandSize;
        const offset = parseFloat(root.querySelector('[data-fit-brand]')?.value);

        if (!Number.isFinite(rawBrandSize)) return fail(root.dataset.errRequiredShoe || 'Please enter your usual size.');
        if (brandSize < 3 || brandSize > 17) return fail(root.dataset.errRangeShoe || 'That size is outside the range we can advise on.');

        size = recommendShoe(brandSize, Number.isFinite(offset) ? offset : 1, classified.sizes);
      } else {
        const L = limitsFor();
        // Validate what she TYPED, in her own units. The converted values are
        // only meaningful against the bands, not against a human-readable range.
        const rawWeight = parseFloat(root.querySelector('[data-fit-weight]')?.value);
        const rawHeight = inputIsMetric()
          ? parseFloat(root.querySelector('[data-fit-height-cm]')?.value)
          : (parseFloat(root.querySelector('[data-fit-height-ft]')?.value) * 12
             + (parseFloat(root.querySelector('[data-fit-height-in]')?.value) || 0));

        if (!Number.isFinite(rawWeight)) return fail(root.dataset.errRequired || 'Please fill in your height and weight.');
        if (rawWeight < L.wMin || rawWeight > L.wMax) {
          return fail(`Please enter a weight between ${L.wMin} and ${L.wMax} ${L.wUnit}.`);
        }
        if (Number.isFinite(rawHeight) && (rawHeight < L.hMin || rawHeight > L.hMax)) {
          return fail(
            inputIsMetric()
              ? `Please enter a height between ${L.hMin} and ${L.hMax} cm.`
              : "Please enter a height between 4'0\" and 7'0\"."
          );
        }

        // Now convert into the unit the bands are written in.
        const weight = readWeight();
        const height = readHeight();
        const age = parseFloat(root.querySelector('[data-fit-age]')?.value) || null;

        size = recommendApparel(bands, weight, height, sizeUpSteps, relaxedFromAge, age, stocked);
      }

      if (!size) return fail(root.dataset.errNoMatch || 'We could not match a size for this item.');

      error.hidden = true;
      resultSize.textContent = size;
      result.hidden = false;
      applyToVariantPicker(size);
    });

    root.querySelector('[data-fit-close]')?.addEventListener('click', () => {
      root.open = false;
      document.querySelector('[data-fit-open]')?.focus({ preventScroll: true });
    });

    root.querySelectorAll('.fit-finder__input').forEach((input) => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit.click();
        }
      });
    });

    addCompactTrigger(root, root.dataset.compactLabel || 'Find my size');
  };

  const boot = () => document.querySelectorAll('[data-fit-finder]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
